import React, { useState, useEffect } from "react";
import { TextField } from "@mui/material";
import PropTypes from "prop-types";

const BoundedNumberField = ({
  id,
  label,
  field,
  value,
  min = -Infinity,
  max = Infinity,
  onSave,
}) => {
  // local temporary string state so user can type freely (including empty)
  const [local, setLocal] = useState(value == null ? "" : String(value));

  // keep local state in sync if parent updates emp (important when parent re-renders)
  useEffect(() => {
    setLocal(value == null ? "" : String(value));
  }, [value]);

  const clamp = (v) => {
    if (v === "") return "";
    const n = Number(v);
    if (Number.isNaN(n)) return "";
    if (n < min) return min;
    if (n > max) return max;
    return n;
  };

  return (
    <TextField
      size="small"
      label={label}
      type="text" // use text so we can display empty and preserve formatting
      value={local}
      onChange={(e) => {
        // allow only digits and optional minus/decimal if needed — adapt regex if you only want integers
        const raw = e.target.value;
        if (raw === "" || /^-?\d*\.?\d*$/.test(raw)) {
          setLocal(raw);
        }
      }}
      onBlur={() => {
        const clamped = clamp(local);
        // update displayed value to clamped (string)
        setLocal(clamped === "" ? "" : String(clamped));
        // call parent save (pass numeric or empty)
        onSave(id, { [field]: clamped === "" ? null : clamped });
      }}
      inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
    />
  );
};
BoundedNumberField.propTypes = {
  /** Employee or record ID used for saving */
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,

  /** Label to display on the text field */
  label: PropTypes.string.isRequired,

  /** The key/field name that will be updated (e.g., 'experienceYears') */
  field: PropTypes.string.isRequired,

  /** Current numeric value (can be null or undefined) */
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  /** Minimum allowed value */
  min: PropTypes.number,

  /** Maximum allowed value */
  max: PropTypes.number,

  /** Function called on blur with (id, { [field]: value }) */
  onSave: PropTypes.func.isRequired,
};

export default BoundedNumberField;
